import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { useFacebookOAuth } from './hooks/useFacebookOAuth';

/**
 * FacebookLogin component initiates Facebook login and fetches user profile information.
 *
 * This component uses the Facebook JavaScript SDK to initialize OAuth, check login status,
 * and trigger the appropriate callback based on the login outcome.
 *
 * @param {Object} props - The component props.
 * @param {function} props.onSuccess - Callback invoked when login and profile fetch are successful.
 * @param {function} props.onError - Callback invoked on login errors.
 * @param {function} props.onNonOAuthError - Callback invoked on non-OAuth related errors.
 * @returns {JSX.Element} The rendered Facebook login button.
 */
export default function FacebookLogin({
	onSuccess,
	onError,
	onNonOAuthError,
}) {
	const { appId, scriptLoadedSuccessfully } = useFacebookOAuth();

	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const onNonOAuthErrorRef = useRef(onNonOAuthError);
	onNonOAuthErrorRef.current = onNonOAuthError;

	useEffect(() => {
		if (!scriptLoadedSuccessfully) return;

		window.fbAsyncInit = function() {
			/*global FB*/
			FB.init({
				appId: appId,
				autoLogAppEvents: true,
				xfbml: true,
				version: 'v16.0',
			});

			FB.getLoginStatus(function(credentialResponse) { // Called after the JS SDK has been initialized.
				console.log('statusChangeCallback');
				if (credentialResponse.status === 'connected') { // Logged into your webpage and Facebook.
					FB.api('/me?fields=id,name,email', function(profileResponse) {
						onSuccessRef.current({ credentialResponse: credentialResponse, profileResponse: profileResponse });
					});
				} else { // Not logged into your webpage or unable to determine login status.
					console.log('Please log into this webpage.');
				}
			});

			FB.login(function(credentialResponse) {
				if (credentialResponse.authResponse) {
					FB.api('/me?fields=id,name,email', function(profileResponse) {
						onSuccessRef.current({ credentialResponse: credentialResponse, profileResponse: profileResponse });
					});
				} else {
					console.log('User cancelled login or did not fully authorize.');
				}
			}, { scope: 'public_profile,email' });
		};
	}, [scriptLoadedSuccessfully, appId]);

	return (
		<Button
			onClick={() => {
				// The onClick handler can be updated as required.
			}}
		>
			Continue with Facebook1
		</Button>
	);
}

FacebookLogin.propTypes = {
	onSuccess: PropTypes.func.isRequired,
	onError: PropTypes.func.isRequired,
	onNonOAuthError: PropTypes.func.isRequired,
};
